// (c) 2026 Planet Q Productions. All Rights Reserved.

import React, { useRef, useEffect } from 'react';

const RunnerMode = ({ 
  backgroundImage,
  characterImage, 
  enemyImage,
  obstacleImage,
  gameTitle,
  gameSpeed,
  storyImages,
  dynamicStories,
  isPlaying,
  onScoreUpdate,
  onGameOver,
  onStageComplete,
}) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const imagesRef = useRef({ bg: null, ch: null, en: null, ob: null });
  const storyImgRef = useRef({});

  const onScoreRef = useRef(onScoreUpdate);
  const onGameOverRef = useRef(onGameOver);
  const onStageRef = useRef(onStageComplete);
  const dynamicStoriesRef = useRef(dynamicStories);
  useEffect(() => { onScoreRef.current = onScoreUpdate; }, [onScoreUpdate]);
  useEffect(() => { onGameOverRef.current = onGameOver; }, [onGameOver]);
  useEffect(() => { onStageRef.current = onStageComplete; }, [onStageComplete]);
  useEffect(() => { dynamicStoriesRef.current = dynamicStories; }, [dynamicStories]);

  // Load story images as they become available
  useEffect(() => {
    if (!storyImages) return;
    Object.entries(storyImages).forEach(([idx, src]) => {
      if (storyImgRef.current[idx]) return; // Already loaded
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { storyImgRef.current[idx] = img; };
      img.src = src;
    });
  }, [storyImages]);

  useEffect(() => {
    const load = (src, key) => {
      if (!src) return;
      const img = new Image();
      if (src.startsWith('http')) img.crossOrigin = 'anonymous';
      img.onload = () => { imagesRef.current[key] = img; };
      img.onerror = () => { console.warn(`RunnerMode: failed to load ${key}`); };
      img.src = src;
    };
    load(backgroundImage, 'bg');
    load(characterImage, 'ch');
    load(enemyImage, 'en');
    load(obstacleImage, 'ob');
  }, [backgroundImage, characterImage, enemyImage, obstacleImage]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      const w = canvas.parentElement?.offsetWidth || window.innerWidth;
      const h = canvas.parentElement?.offsetHeight || window.innerHeight;
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w; canvas.height = h;
      }
      return { w, h };
    };

    // Perspective projection for placing game objects
    const PERSP = 0.013;

    // ── HOLEY MOLEY STORY SCENES (10 levels) ──
    const holeyMoleyStories = [
      { title: "HOME SWEET HOLE", desc: "The mole dives into his cozy dirty apartment...", scene: "Relaxing on a beat-up couch, mud everywhere, home at last.", emoji: "🏠", colors: ['#8B4513','#654321','#3d2b1f'] },
      { title: "MOLEY'S GROCERY", desc: "Time to stock up at the underground grocery store...", scene: "Grabbing worm cans and beetle chips off dusty shelves.", emoji: "🛒", colors: ['#2d5016','#4a7c23','#1a3d0a'] },
      { title: "THE MUDDY PUB", desc: "A well-earned drink at the underground bar...", scene: "Clinking mud-ales with fellow moles. What a day!", emoji: "🍺", colors: ['#4a2800','#6b3a00','#2d1a00'] },
      { title: "MOLE WORKSHOP", desc: "Back to the workshop to build new gadgets...", scene: "Welding goggles on, sparks flying, inventing escape tools.", emoji: "🔧", colors: ['#333','#555','#1a1a1a'] },
      { title: "UNDERGROUND LIBRARY", desc: "Studying old maps in the mole library...", scene: "Ancient tunnels, dusty books, planning the next route.", emoji: "📚", colors: ['#2a1a3d','#4a2d6b','#1a0d2d'] },
      { title: "MOLE SPA & POOL", desc: "A mud bath at the underground spa...", scene: "Steaming mud pools, total relaxation. Ahhh...", emoji: "♨️", colors: ['#1a4a5a','#0d3040','#0a2530'] },
      { title: "COMMAND CENTER", desc: "The mole HQ — planning the next big run...", scene: "Holographic maps, blinking screens, tactical planning.", emoji: "🖥️", colors: ['#0a1a2d','#0d2540','#061220'] },
      { title: "MOLE HOSPITAL", desc: "Getting patched up at Dr. Digger's clinic...", scene: "Bandages, healing potions, ready for round two.", emoji: "🏥", colors: ['#2d3d4a','#1a2d3a','#0d1a25'] },
      { title: "TRAINING GYM", desc: "Pumping iron at the underground gym...", scene: "Digging reps, claw push-ups, getting STRONGER.", emoji: "💪", colors: ['#4a1a1a','#6b2d2d','#2d0d0d'] },
      { title: "THE PENTHOUSE", desc: "The ultimate reward — the mole penthouse!", scene: "Gold-lined tunnels, velvet furniture, the mole dream life.", emoji: "👑", colors: ['#4a3d00','#6b5a00','#2d2500'] },
    ];
    const isHoleyMoley = (gameTitle || '').toLowerCase().includes('holey moley');

    const ROAD_W = 0.48;
    const MAX_Z = 180;        // Objects start MUCH further back

    const proj = (z, w, h, hY) => {
      const sc = 1 / (1 + z * PERSP);
      return { y: hY + (h - hY) * sc, sc, rHalf: (w * ROAD_W / 2) * sc };
    };

    const laneOff = [-0.6, 0, 0.6];

    // Forward-zoom constants
    const ZOOM_RANGE = 0.2;      // Zoom from 1.0x to 1.2x
    const VP_X_FRAC = 0.5;       // Vanishing point at center-X
    const VP_Y_FRAC = 0.33;      // Vanishing point at ~1/3 from top

    // Speed multiplier from gameSpeed prop
    const speedMultiplier = gameSpeed === 'slow' ? 0.55 : gameSpeed === 'fast' ? 1.6 : 1.0;

    const s = {
      frame: 0, lane: 1,
      jumping: false, jumpVel: 0, jumpY: 0,
      dist: 0, speed: 3 * speedMultiplier, score: 0,
      stage: 1, stageScore: 0, stageThreshold: 400,
      hazards: [], spawnTimer: 0,
      lives: 3, invincible: 0,
      gameOver: false, celebrating: false, celebTimer: 0,
      storyPhase: false, storyTimer: 0, storyWaitingForInput: false,
      introPhase: true, introTimer: 0, introWaitingForInput: false,
      endingPhase: false, endingTimer: 0,
      particles: [],
      smoothLane: 0,
      zoomPhase: 0,
      maxStages: 10,
    };

    const onKey = (e) => {
      // Allow advancing story/intro/ending with Enter or Space
      if (e.key === 'Enter' || e.key === ' ') {
        if (s.introWaitingForInput) { s.introPhase = false; s.introTimer = 0; s.introWaitingForInput = false; return; }
        if (s.storyWaitingForInput) { s.storyPhase = false; s.storyTimer = 0; s.storyWaitingForInput = false; return; }
        if (s.endingPhase) { s.endingPhase = false; if (onGameOverRef.current) onGameOverRef.current(Math.floor(s.score), true); return; }
      }
      if (s.gameOver || s.celebrating || s.storyPhase || s.introPhase || s.endingPhase) return;
      if (e.key === 'ArrowLeft' || e.key === 'a') { if (s.lane > 0) s.lane--; }
      if (e.key === 'ArrowRight' || e.key === 'd') { if (s.lane < 2) s.lane++; }
      if ((e.key === 'ArrowUp' || e.key === 'w' || e.key === ' ') && !s.jumping) {
        s.jumping = true; s.jumpVel = -13;
      }
    };
    window.addEventListener('keydown', onKey);

    const doJump = () => {
      if (!s.jumping && !s.gameOver && !s.celebrating && !s.storyPhase && !s.introPhase && !s.endingPhase) {
        s.jumping = true; s.jumpVel = -13;
      }
    };
    const advanceStory = () => {
      if (s.introWaitingForInput) { s.introPhase = false; s.introTimer = 0; s.introWaitingForInput = false; }
      else if (s.storyWaitingForInput) { s.storyPhase = false; s.storyTimer = 0; s.storyWaitingForInput = false; }
      else if (s.endingPhase) { s.endingPhase = false; if (onGameOverRef.current) onGameOverRef.current(Math.floor(s.score), true); }
    };
    window.gameActions = {
      moveUp: doJump, moveDown: () => {},
      moveLeft: () => { if (s.lane > 0 && !s.gameOver) s.lane--; },
      moveRight: () => { if (s.lane < 2 && !s.gameOver) s.lane++; },
      shoot: doJump, jump: doJump,
      advanceStory,
    };

    // Draw environment image zoomed and centered on vanishing point
    const drawEnvZoomed = (img, w, h, zoom) => {
      const imgR = img.width / img.height;
      const canR = w / h;
      let sx = 0, sy = 0, sw = img.width, sh = img.height;
      if (imgR > canR) { sw = img.height * canR; sx = (img.width - sw) / 2; }
      else { sh = img.width / canR; sy = (img.height - sh) / 2; }
      const dw = w * zoom;
      const dh = h * zoom;
      const dx = -(dw - w) * VP_X_FRAC;
      const dy = -(dh - h) * VP_Y_FRAC;
      ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
    };

    let running = true;

    const loop = () => {
      if (!running) return;

      // Check pause
      if (window.gamePaused) {
        animationRef.current = requestAnimationFrame(loop);
        return;
      }

      s.frame++;
      const { w, h } = resize();
      const images = imagesRef.current;
      const hY = h * 0.35;
      const cx = w / 2;

      ctx.clearRect(0, 0, w, h);

      // ──── INTRO STORY (before gameplay starts) ────
      if (s.introPhase) {
        s.introTimer++;
        const t = s.introTimer;

        // Background
        drawFullBg(ctx, images.bg, w, h, drawEnvZoomed);
        ctx.fillStyle = 'rgba(0,0,0,0.7)';
        ctx.fillRect(0, 0, w, h);

        // Get intro story from dynamic stories or generate a default
        let introStory;
        if (dynamicStoriesRef.current && dynamicStoriesRef.current[0]) {
          const ds = dynamicStoriesRef.current[0];
          introStory = {
            title: (gameTitle || 'THE ADVENTURE BEGINS').toUpperCase(),
            desc: ds.desc || 'Your journey starts here...',
            scene: ds.scene || 'A new adventure awaits.',
          };
        } else {
          introStory = {
            title: (gameTitle || 'THE ADVENTURE BEGINS').toUpperCase(),
            desc: 'Your journey is about to begin...',
            scene: 'Danger lurks around every corner. Stay sharp and keep running!',
          };
        }

        // Animated title entrance
        const titleAlpha = Math.min(1, t / 30);
        ctx.globalAlpha = titleAlpha;
        ctx.fillStyle = '#FFD700';
        ctx.font = `bold ${Math.min(w * 0.07, 48)}px Rajdhani, sans-serif`;
        ctx.textAlign = 'center';
        ctx.fillText(introStory.title, cx, h * 0.22);

        // Decorative line
        const lineW = Math.min(w * 0.5, 300) * Math.min(1, t / 40);
        ctx.strokeStyle = 'rgba(255,215,0,0.5)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(cx - lineW / 2, h * 0.26);
        ctx.lineTo(cx + lineW / 2, h * 0.26);
        ctx.stroke();

        // Story text with typewriter
        if (t > 20) {
          const textAlpha = Math.min(1, (t - 20) / 20);
          ctx.globalAlpha = textAlpha;
          ctx.fillStyle = '#ddd';
          ctx.font = `${Math.min(w * 0.035, 22)}px Rajdhani, sans-serif`;
          ctx.fillText(introStory.desc, cx, h * 0.34);
        }

        if (t > 40) {
          const visibleChars = Math.min(introStory.scene.length, Math.floor((t - 40) * 1.2));
          ctx.globalAlpha = Math.min(1, (t - 40) / 15);
          ctx.fillStyle = 'rgba(255,255,255,0.7)';
          ctx.font = `italic ${Math.min(w * 0.03, 18)}px Rajdhani, sans-serif`;
          ctx.fillText(introStory.scene.substring(0, visibleChars), cx, h * 0.42);
        }

        // Character sprite in center
        if (t > 30 && images.ch) {
          const charAlpha = Math.min(1, (t - 30) / 20);
          ctx.globalAlpha = charAlpha;
          const charSize = Math.min(w * 0.2, 140);
          const bob = Math.sin(t * 0.04) * 5;
          ctx.drawImage(images.ch, cx - charSize / 2, h * 0.48 + bob, charSize, charSize * 1.2);
        }

        // Speed indicator
        if (t > 50) {
          ctx.globalAlpha = Math.min(1, (t - 50) / 15);
          const speedLabel = gameSpeed === 'slow' ? 'SLOW' : gameSpeed === 'fast' ? 'FAST' : 'MEDIUM';
          const speedColor = gameSpeed === 'slow' ? '#00FF88' : gameSpeed === 'fast' ? '#FF4444' : '#FFD700';
          ctx.fillStyle = speedColor;
          ctx.font = `bold ${Math.min(w * 0.025, 16)}px Rajdhani, sans-serif`;
          ctx.fillText(`SPEED: ${speedLabel}`, cx, h * 0.73);
        }

        // "Press Start" button
        if (t > 70) {
          s.introWaitingForInput = true;
          const flash = Math.sin(t * 0.06) * 0.3 + 0.7;
          ctx.globalAlpha = flash;
          ctx.fillStyle = '#00F0FF';
          ctx.font = `bold ${Math.min(w * 0.04, 24)}px Rajdhani, sans-serif`;
          ctx.fillText('PRESS START TO BEGIN', cx, h * 0.82);

          // Draw a button-like shape behind text
          ctx.globalAlpha = flash * 0.15;
          const btnW = Math.min(w * 0.45, 280);
          ctx.fillStyle = '#00F0FF';
          ctx.beginPath();
          ctx.roundRect(cx - btnW / 2, h * 0.78, btnW, 30, 15);
          ctx.fill();
        }

        // Stage indicator bar
        ctx.globalAlpha = 0.6;
        ctx.fillStyle = 'rgba(0,0,0,0.5)'; ctx.fillRect(0, 0, w, 32);
        ctx.fillStyle = '#FFD700'; ctx.font = 'bold 13px Rajdhani, sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('PROLOGUE', cx, 20);

        ctx.globalAlpha = 1;
        animationRef.current = requestAnimationFrame(loop);
        return;
      }

      // ──── ENDING STORY (after completing all stages) ────
      if (s.endingPhase) {
        s.endingTimer++;
        const t = s.endingTimer;

        drawFullBg(ctx, images.bg, w, h, drawEnvZoomed);
        ctx.fillStyle = 'rgba(0,0,0,0.75)';
        ctx.fillRect(0, 0, w, h);

        // Victory particles
        if (t % 4 === 0) {
          for (let i = 0; i < 4; i++)
            s.particles.push({ x: Math.random() * w, y: h + 10, vx: (Math.random() - 0.5) * 4, vy: -Math.random() * 8 - 3, life: 50, color: ['#FFD700','#FF69B4','#00FF00','#00BFFF','#FF4400'][i % 5] });
        }
        drawParticles(ctx, s.particles, 50);

        // Title
        const titleAlpha = Math.min(1, t / 30);
        ctx.globalAlpha = titleAlpha;
        ctx.fillStyle = '#FFD700';
        ctx.font = `bold ${Math.min(w * 0.08, 56)}px Rajdhani, sans-serif`;
        ctx.textAlign = 'center';
        ctx.fillText('VICTORY!', cx, h * 0.2);

        ctx.fillStyle = '#fff';
        ctx.font = `${Math.min(w * 0.04, 26)}px Rajdhani, sans-serif`;
        ctx.fillText(`${gameTitle || 'PLANET Q'} — COMPLETE`, cx, h * 0.28);

        // Final score
        if (t > 20) {
          ctx.globalAlpha = Math.min(1, (t - 20) / 20);
          ctx.fillStyle = '#00F0FF';
          ctx.font = `bold ${Math.min(w * 0.06, 40)}px Rajdhani, sans-serif`;
          ctx.fillText(`FINAL SCORE: ${Math.floor(s.score)}`, cx, h * 0.4);
        }

        // Ending story text
        if (t > 40) {
          let endText = 'The adventure comes to an end. You are a true champion!';
          if (dynamicStoriesRef.current && dynamicStoriesRef.current[9]) {
            endText = dynamicStoriesRef.current[9].scene || endText;
          }
          const visibleChars = Math.min(endText.length, Math.floor((t - 40) * 1.0));
          ctx.globalAlpha = Math.min(1, (t - 40) / 15);
          ctx.fillStyle = 'rgba(255,255,255,0.8)';
          ctx.font = `italic ${Math.min(w * 0.03, 20)}px Rajdhani, sans-serif`;
          ctx.fillText(endText.substring(0, visibleChars), cx, h * 0.52);
        }

        // Character celebration
        if (t > 30 && images.ch) {
          ctx.globalAlpha = Math.min(1, (t - 30) / 20);
          const charSize = Math.min(w * 0.2, 140);
          const bob = Math.sin(t * 0.06) * 8;
          ctx.drawImage(images.ch, cx - charSize / 2, h * 0.58 + bob, charSize, charSize * 1.2);
        }

        // "Press to Finish" prompt
        if (t > 80) {
          const flash = Math.sin(t * 0.06) * 0.3 + 0.7;
          ctx.globalAlpha = flash;
          ctx.fillStyle = '#FFD700';
          ctx.font = `bold ${Math.min(w * 0.035, 22)}px Rajdhani, sans-serif`;
          ctx.fillText('PRESS START TO FINISH', cx, h * 0.88);
        }

        ctx.globalAlpha = 1;
        animationRef.current = requestAnimationFrame(loop);
        return;
      }

      // ──── GAME OVER ────
      if (s.gameOver) {
        drawFullBg(ctx, images.bg, w, h, drawEnvZoomed);
        ctx.fillStyle = 'rgba(0,0,0,0.75)'; ctx.fillRect(0, 0, w, h);
        if (onGameOverRef.current) onGameOverRef.current(Math.floor(s.score));
        return;
      }

      // ──── CELEBRATING ────
      if (s.celebrating) {
        s.celebTimer++;
        drawFullBg(ctx, images.bg, w, h, drawEnvZoomed);
        ctx.fillStyle = 'rgba(0,0,0,0.55)'; ctx.fillRect(0, 0, w, h);
        ctx.fillStyle = '#FFD700';
        ctx.font = `bold ${Math.min(w * 0.06, 42)}px Rajdhani, sans-serif`;
        ctx.textAlign = 'center';
        ctx.fillText(`STAGE ${s.stage - 1} COMPLETE!`, cx, h * 0.35);
        ctx.fillStyle = '#fff';
        ctx.font = `${Math.min(w * 0.04, 26)}px Rajdhani, sans-serif`;
        ctx.fillText(`Score: ${Math.floor(s.score)}`, cx, h * 0.43);
        if (s.frame % 3 === 0) {
          for (let i = 0; i < 3; i++)
            s.particles.push({ x: Math.random() * w, y: h * 0.5, vx: (Math.random() - 0.5) * 7, vy: -Math.random() * 7 - 2, life: 35, color: ['#FFD700','#FF69B4','#00FF00','#00BFFF'][i % 4] });
        }
        drawParticles(ctx, s.particles, 35);
        if (s.celebTimer > 140) {
          s.celebrating = false; s.celebTimer = 0; s.particles = [];
          // Check if game is complete (all stages done)
          if (s.stage > s.maxStages) {
            s.endingPhase = true;
            s.endingTimer = 0;
            animationRef.current = requestAnimationFrame(loop);
            return;
          }
          // Transition to story cutscene for all Runner games
          const hasStories = isHoleyMoley || (dynamicStoriesRef.current && dynamicStoriesRef.current.length > 0);
          if (hasStories && (s.stage - 1) <= 10) {
            s.storyPhase = true;
            s.storyTimer = 0;
            s.storyWaitingForInput = false;
          }
          if (onStageRef.current) onStageRef.current(s.stage);
        }
        animationRef.current = requestAnimationFrame(loop);
        return;
      }

      // ──── STORY CUTSCENE ────
      if (s.storyPhase) {
        s.storyTimer++;
        const storyIdx = Math.min((s.stage - 2), 9);
        const safeIdx = Math.max(0, storyIdx);

        // Pick story: dynamic (GPT-generated) > Holey Moley fallback > generic
        let story;
        if (dynamicStoriesRef.current && dynamicStoriesRef.current[safeIdx]) {
          const ds = dynamicStoriesRef.current[safeIdx];
          story = {
            title: ds.title || `STAGE ${s.stage - 1} REST`,
            desc: ds.desc || 'A moment of peace...',
            scene: ds.scene || 'Resting before the next challenge.',
            colors: (ds.colors && ds.colors.length === 3) ? ds.colors : ['#1a2a3a','#0d1a2a','#060d15'],
          };
        } else if (isHoleyMoley && holeyMoleyStories[safeIdx]) {
          story = holeyMoleyStories[safeIdx];
        } else {
          story = { title: `SAFE ZONE ${s.stage - 1}`, desc: 'You found a safe spot to rest...', scene: 'Catching your breath before the next stage.', colors: ['#1a2a3a','#0d1a2a','#060d15'] };
        }

        const t = s.storyTimer;

        // Dark background with story colors
        const grad = ctx.createLinearGradient(0, 0, 0, h);
        grad.addColorStop(0, story.colors[0]);
        grad.addColorStop(0.5, story.colors[1]);
        grad.addColorStop(1, story.colors[2]);
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, w, h);

        // Animated hole effect — expanding circle from center bottom
        const holeProgress = Math.min(1, t / 40);
        const holeR = holeProgress * Math.min(w, h) * 0.35;
        const holeCx = cx;
        const holeCy = h * 0.55;

        // Draw ground with hole
        ctx.fillStyle = '#2a1a0a';
        ctx.fillRect(0, h * 0.65, w, h * 0.35);
        // The hole
        ctx.fillStyle = '#000';
        ctx.beginPath();
        ctx.ellipse(holeCx, h * 0.65, holeR, holeR * 0.3, 0, 0, Math.PI * 2);
        ctx.fill();
        // Dirt ring around hole
        ctx.strokeStyle = '#5a3a1a';
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.ellipse(holeCx, h * 0.65, holeR + 3, holeR * 0.3 + 2, 0, 0, Math.PI * 2);
        ctx.stroke();

        // Mole character diving into hole (phase 1: 0-30 frames — fast dive)
        if (t < 30) {
          const diveT = t / 30;
          const moleY = h * 0.4 + diveT * h * 0.28; // Moving down
          const moleScale = 1 - diveT * 0.5; // Shrinking
          const charSize = Math.min(w * 0.15, 100) * moleScale;
          if (images.ch) {
            ctx.globalAlpha = 1 - diveT * 0.3;
            ctx.drawImage(images.ch, holeCx - charSize / 2, moleY - charSize, charSize, charSize * 1.2);
            ctx.globalAlpha = 1;
          } else {
            ctx.fillStyle = '#00E0FF';
            ctx.beginPath();
            ctx.arc(holeCx, moleY, charSize * 0.3 * moleScale, 0, Math.PI * 2);
            ctx.fill();
          }
        }

        // Story text (fade in after mole dives: frame 25+)
        if (t > 25) {
          const fadeIn = Math.min(1, (t - 25) / 15);
          ctx.globalAlpha = fadeIn;

          // Story title
          ctx.fillStyle = '#FFD700';
          ctx.font = `bold ${Math.min(w * 0.06, 38)}px Rajdhani, sans-serif`;
          ctx.textAlign = 'center';
          ctx.fillText(story.title, cx, h * 0.2);

          // Decorative line
          const lineW = ctx.measureText(story.title).width;
          ctx.strokeStyle = 'rgba(255,215,0,0.4)';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(cx - lineW / 2 - 20, h * 0.23);
          ctx.lineTo(cx + lineW / 2 + 20, h * 0.23);
          ctx.stroke();

          // Story description
          ctx.fillStyle = '#ddd';
          ctx.font = `${Math.min(w * 0.035, 22)}px Rajdhani, sans-serif`;
          ctx.fillText(story.desc, cx, h * 0.3);

          // Scene description (with typewriter effect)
          const visibleChars = Math.min(story.scene.length, Math.floor((t - 35) * 1.5));
          if (visibleChars > 0) {
            ctx.fillStyle = 'rgba(255,255,255,0.7)';
            ctx.font = `italic ${Math.min(w * 0.03, 18)}px Rajdhani, sans-serif`;
            ctx.fillText(story.scene.substring(0, visibleChars), cx, h * 0.37);
          }

          // AI-generated story scene image (or fallback canvas drawing)
          if (t > 40) {
            const sceneAlpha = Math.min(1, (t - 40) / 20);
            const storyImg = storyImgRef.current[storyIdx];

            if (storyImg) {
              // Render the AI-generated image with rounded corners and border
              ctx.globalAlpha = sceneAlpha;
              const imgW = Math.min(w * 0.6, 400);
              const imgH = imgW * 0.65;
              const imgX = cx - imgW / 2;
              const imgY = h * 0.4;

              // Rounded rect clip
              const radius = 12;
              ctx.save();
              ctx.beginPath();
              ctx.moveTo(imgX + radius, imgY);
              ctx.lineTo(imgX + imgW - radius, imgY);
              ctx.quadraticCurveTo(imgX + imgW, imgY, imgX + imgW, imgY + radius);
              ctx.lineTo(imgX + imgW, imgY + imgH - radius);
              ctx.quadraticCurveTo(imgX + imgW, imgY + imgH, imgX + imgW - radius, imgY + imgH);
              ctx.lineTo(imgX + radius, imgY + imgH);
              ctx.quadraticCurveTo(imgX, imgY + imgH, imgX, imgY + imgH - radius);
              ctx.lineTo(imgX, imgY + radius);
              ctx.quadraticCurveTo(imgX, imgY, imgX + radius, imgY);
              ctx.clip();
              ctx.drawImage(storyImg, imgX, imgY, imgW, imgH);
              ctx.restore();

              // Glow border
              ctx.strokeStyle = `rgba(255, 215, 0, ${0.4 + Math.sin(t * 0.05) * 0.15})`;
              ctx.lineWidth = 2;
              ctx.beginPath();
              ctx.moveTo(imgX + radius, imgY);
              ctx.lineTo(imgX + imgW - radius, imgY);
              ctx.quadraticCurveTo(imgX + imgW, imgY, imgX + imgW, imgY + radius);
              ctx.lineTo(imgX + imgW, imgY + imgH - radius);
              ctx.quadraticCurveTo(imgX + imgW, imgY + imgH, imgX + imgW - radius, imgY + imgH);
              ctx.lineTo(imgX + radius, imgY + imgH);
              ctx.quadraticCurveTo(imgX, imgY + imgH, imgX, imgY + imgH - radius);
              ctx.lineTo(imgX, imgY + radius);
              ctx.quadraticCurveTo(imgX, imgY, imgX + radius, imgY);
              ctx.stroke();
            } else {
              // Fallback: canvas-drawn room while image loads
              ctx.globalAlpha = sceneAlpha * 0.6;
              const roomW = w * 0.5;
              const roomH = h * 0.22;
              const roomX = cx - roomW / 2;
              const roomY = h * 0.42;

              ctx.fillStyle = `rgba(${parseInt(story.colors[0].slice(1,3),16)}, ${parseInt(story.colors[0].slice(3,5),16)}, ${parseInt(story.colors[0].slice(5,7),16)}, 0.4)`;
              ctx.fillRect(roomX, roomY, roomW, roomH);
              ctx.strokeStyle = 'rgba(255,255,255,0.2)';
              ctx.lineWidth = 1;
              ctx.strokeRect(roomX, roomY, roomW, roomH);

              const lightGrad = ctx.createRadialGradient(cx, roomY + roomH * 0.4, 0, cx, roomY + roomH * 0.4, roomW * 0.4);
              lightGrad.addColorStop(0, 'rgba(255,200,100,0.15)');
              lightGrad.addColorStop(1, 'rgba(0,0,0,0)');
              ctx.fillStyle = lightGrad;
              ctx.fillRect(roomX, roomY, roomW, roomH);

              if (images.ch) {
                const miniSize = Math.min(w * 0.08, 55);
                ctx.globalAlpha = sceneAlpha * 0.9;
                ctx.drawImage(images.ch, cx - miniSize / 2, roomY + roomH - miniSize * 1.1, miniSize, miniSize);
              }

              // "Generating scene..." indicator
              ctx.globalAlpha = 0.5 + Math.sin(t * 0.08) * 0.2;
              ctx.fillStyle = '#00bfff';
              ctx.font = `${Math.min(w * 0.025, 14)}px Rajdhani, sans-serif`;
              ctx.textAlign = 'center';
              ctx.fillText('Generating scene image...', cx, roomY + roomH + 20);
            }
          }

          ctx.globalAlpha = 1;
        }

        // "PRESS START TO CONTINUE" prompt (after text fully revealed)
        if (t > 100) {
          s.storyWaitingForInput = true;
          const flash = Math.sin(t * 0.06) * 0.3 + 0.7;
          ctx.globalAlpha = flash;
          ctx.fillStyle = '#00F0FF';
          ctx.font = `bold ${Math.min(w * 0.035, 20)}px Rajdhani, sans-serif`;
          ctx.textAlign = 'center';
          ctx.fillText('PRESS START TO CONTINUE', cx, h * 0.88);

          ctx.globalAlpha = flash * 0.15;
          const btnW = Math.min(w * 0.5, 300);
          ctx.fillStyle = '#00F0FF';
          ctx.beginPath();
          ctx.roundRect(cx - btnW / 2, h * 0.85, btnW, 28, 14);
          ctx.fill();
        }

        // Stage indicator
        ctx.fillStyle = 'rgba(0,0,0,0.5)'; ctx.fillRect(0, 0, w, 32);
        ctx.fillStyle = '#FFD700'; ctx.font = 'bold 13px Rajdhani, sans-serif'; ctx.textAlign = 'center';
        ctx.fillText(`STAGE ${s.stage - 1} → STAGE ${s.stage}`, cx, 20);

        // Story only advances when user presses start (no auto-advance)

        animationRef.current = requestAnimationFrame(loop);
        return;
      }

      // ──── UPDATE ────
      s.dist += s.speed;
      s.score += s.speed * 0.12;
      s.stageScore += s.speed * 0.12;
      if (onScoreRef.current) onScoreRef.current(Math.floor(s.score));
      if (s.invincible > 0) s.invincible--;

      // Forward zoom phase (0 → 1, cycling)
      s.zoomPhase = (s.zoomPhase + 0.0018 * s.speed) % 1.0;

      s.smoothLane += (laneOff[s.lane] - s.smoothLane) * 0.14;
      if (s.frame === 1) s.smoothLane = laneOff[s.lane];

      if (s.jumping) {
        s.jumpVel += 0.75;
        s.jumpY += s.jumpVel;
        if (s.jumpY >= 0) { s.jumpY = 0; s.jumping = false; }
      }

      if (s.stageScore >= s.stageThreshold) {
        s.stage++; s.stageScore = 0; s.stageThreshold += 200;
        s.speed = Math.min(s.speed + 0.4 * speedMultiplier, 9 * speedMultiplier);
        s.celebrating = true; s.hazards = [];
        animationRef.current = requestAnimationFrame(loop);
        return;
      }

      s.spawnTimer++;
      const interval = Math.max(18, 48 - s.stage * 4);
      if (s.spawnTimer >= interval) {
        s.spawnTimer = 0;
        s.hazards.push({
          lane: Math.floor(Math.random() * 3),
          z: MAX_Z + Math.random() * 20,
          type: Math.random() > 0.4 ? 'enemy' : 'obstacle',
          fr: 0,
          wobbleOff: Math.random() * Math.PI * 2,
        });
      }

      s.hazards.forEach(hz => { hz.z -= s.speed * 0.75; hz.fr++; });
      s.hazards = s.hazards.filter(hz => hz.z > -8);

      if (s.invincible <= 0) {
        for (const hz of s.hazards) {
          if (hz.lane === s.lane && hz.z > -3 && hz.z < 5) {
            if (s.jumpY < -25) continue;
            s.lives--; s.invincible = 50;
            const hitP = proj(0, w, h, hY);
            const hitX = cx + s.smoothLane * hitP.rHalf;
            for (let i = 0; i < 8; i++)
              s.particles.push({ x: hitX, y: hitP.y - 30, vx: (Math.random() - 0.5) * 6, vy: (Math.random() - 0.5) * 6, life: 25, color: '#f44' });
            if (s.lives <= 0) s.gameOver = true;
            break;
          }
        }
      }

      // ──── DRAW ────

      // Stage color filters — gives INSTANT visual change per stage
      const stageFilters = [
        'none',                                      // Stage 1: original
        'sepia(0.3) hue-rotate(20deg)',              // Stage 2: warm sunset
        'hue-rotate(200deg) brightness(0.8)',        // Stage 3: night blue
        'hue-rotate(90deg) saturate(1.3)',           // Stage 4: alien green
        'sepia(0.5) hue-rotate(-20deg) contrast(1.1)', // Stage 5: fiery
        'hue-rotate(170deg) brightness(0.7) saturate(0.8)', // Stage 6: deep ocean
        'invert(0.1) hue-rotate(280deg)',            // Stage 7: space purple
        'hue-rotate(40deg) brightness(1.1) saturate(1.2)', // Stage 8: dawn
        'hue-rotate(310deg) contrast(1.2)',          // Stage 9: eclipse
        'hue-rotate(130deg) saturate(1.5) brightness(0.9)', // Stage 10: neon
      ];
      ctx.filter = stageFilters[(s.stage - 1) % stageFilters.length] || 'none';

      // 1) Environment — full-screen with forward zoom animation
      if (images.bg) {
        const p1 = s.zoomPhase;
        const p2 = (s.zoomPhase + 0.5) % 1.0;
        const z1 = 1.0 + p1 * ZOOM_RANGE;
        const z2 = 1.0 + p2 * ZOOM_RANGE;
        const fade = Math.abs(Math.sin(p1 * Math.PI));
        ctx.globalAlpha = fade;
        drawEnvZoomed(images.bg, w, h, z1);
        ctx.globalAlpha = 1.0 - fade;
        drawEnvZoomed(images.bg, w, h, z2);
        ctx.globalAlpha = 1.0;
      } else {
        const grad = ctx.createLinearGradient(0, 0, 0, h);
        grad.addColorStop(0, '#080820');
        grad.addColorStop(0.35, '#1a1a4a');
        grad.addColorStop(0.55, '#2d3d5d');
        grad.addColorStop(0.7, '#1a4a1a');
        grad.addColorStop(1, '#0d2d0d');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, w, h);
      }

      // Reset filter for game elements (character, enemies, HUD)
      ctx.filter = 'none';

      // 2) Hazards (far → near) — depth fog makes them blend into environment
      const sorted = [...s.hazards].sort((a, b) => b.z - a.z);
      sorted.forEach(hz => {
        if (hz.z < -2 || hz.z > MAX_Z + 30) return;
        const p = proj(Math.max(hz.z, 0.1), w, h, hY);
        let hx = cx + laneOff[hz.lane] * p.rHalf;
        const sz = Math.max(10, p.sc * w * 0.18);

        // Depth fog: objects further away are more transparent
        const depthFog = Math.max(0.15, Math.min(1.0, 1.0 - (hz.z / MAX_Z) * 0.7));
        ctx.globalAlpha = depthFog;

        // Ground contact shadow FIRST (drawn before the object)
        const shadowAlpha = depthFog * 0.35;
        ctx.fillStyle = `rgba(0,0,0,${shadowAlpha})`;
        ctx.beginPath();
        ctx.ellipse(hx, p.y + 2, sz * 0.4, sz * 0.06 + 1, 0, 0, Math.PI * 2);
        ctx.fill();

        if (hz.type === 'enemy') {
          hx += Math.sin(hz.wobbleOff + hz.fr * 0.08) * sz * 0.2;
          if (images.en) {
            ctx.save();
            ctx.translate(hx, p.y);
            const pulse = 1 + Math.sin(hz.fr * 0.15) * 0.06;
            ctx.scale(pulse, pulse);
            ctx.drawImage(images.en, -sz / 2, -sz, sz, sz);
            ctx.restore();
          } else {
            ctx.fillStyle = '#FF2222';
            ctx.shadowColor = '#f00'; ctx.shadowBlur = 8 * p.sc;
            ctx.save(); ctx.translate(hx, p.y);
            ctx.rotate(Math.PI / 4 + Math.sin(hz.fr * 0.1) * 0.12);
            const rs = sz * 0.45;
            ctx.fillRect(-rs, -rs, rs * 2, rs * 2);
            ctx.restore(); ctx.shadowBlur = 0;
          }
        } else {
          if (images.ob) {
            ctx.drawImage(images.ob, hx - sz * 0.5, p.y - sz, sz, sz);
          } else {
            ctx.fillStyle = '#FF8C00';
            ctx.shadowColor = '#FF8C00'; ctx.shadowBlur = 5 * p.sc;
            ctx.fillRect(hx - sz * 0.45, p.y - sz * 0.5, sz * 0.9, sz * 0.5);
            ctx.shadowBlur = 0;
          }
        }
        ctx.globalAlpha = 1;
      });

      // 3) Player character — BIGGER
      const pp = proj(0.5, w, h, hY);
      const px = cx + s.smoothLane * pp.rHalf;
      const charW = Math.min(w * 0.2, 160);
      const charH = charW * 1.4;
      const py = pp.y + s.jumpY * 0.65;
      const blink = s.invincible > 0 && s.frame % 6 < 3;

      if (!blink) {
        if (images.ch) {
          ctx.save();
          ctx.translate(px, py);
          const bob = Math.abs(Math.sin(s.frame * 0.15)) * 4;
          const lean = Math.sin(s.frame * 0.1) * 0.03;
          ctx.rotate(lean);
          ctx.drawImage(images.ch, -charW / 2, -charH + bob, charW, charH);
          ctx.restore();
        } else {
          ctx.fillStyle = '#00E0FF'; ctx.shadowColor = '#00F0FF'; ctx.shadowBlur = 15;
          ctx.beginPath();
          ctx.moveTo(px, py - charH);
          ctx.lineTo(px - charW * 0.4, py);
          ctx.lineTo(px + charW * 0.4, py);
          ctx.closePath(); ctx.fill();
          ctx.shadowBlur = 0;
        }
      }
      // Player shadow
      ctx.fillStyle = 'rgba(0,0,0,0.35)';
      ctx.beginPath();
      ctx.ellipse(px, pp.y + 5, charW * 0.4, 7, 0, 0, Math.PI * 2);
      ctx.fill();

      // 5) Particles
      drawParticles(ctx, s.particles, 25);

      // 6) HUD
      ctx.fillStyle = 'rgba(0,0,0,0.5)'; ctx.fillRect(0, 0, w, 32);
      ctx.fillStyle = '#fff'; ctx.font = 'bold 13px Rajdhani, sans-serif'; ctx.textAlign = 'left';
      ctx.fillText(`STAGE ${s.stage}`, 10, 20);
      for (let i = 0; i < s.lives; i++) { ctx.fillStyle = '#f00'; ctx.fillText('\u2665', 85 + i * 16, 20); }
      ctx.textAlign = 'right'; ctx.fillStyle = '#0ff';
      ctx.fillText(`${Math.floor(s.score)}`, w - 10, 20);
      const bW = w * 0.2; const bX = (w - bW) / 2;
      ctx.fillStyle = 'rgba(255,255,255,0.15)'; ctx.fillRect(bX, 24, bW, 4);
      ctx.fillStyle = '#00F0FF'; ctx.fillRect(bX, 24, bW * Math.min(s.stageScore / s.stageThreshold, 1), 4);

      animationRef.current = requestAnimationFrame(loop);
    };

    animationRef.current = requestAnimationFrame(loop);
    return () => {
      running = false;
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener('keydown', onKey);
      delete window.gameActions;
    };
  }, [isPlaying]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="runner-canvas"
      className="absolute inset-0 w-full h-full"
      style={{ touchAction: 'none' }}
      onClick={() => { if (window.gameActions?.advanceStory) window.gameActions.advanceStory(); }}
    />
  );
};

function drawFullBg(ctx, bgImg, w, h, drawEnvZoomed) {
  if (bgImg) {
    drawEnvZoomed(bgImg, w, h, 1.0);
  } else {
    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, '#080820'); grad.addColorStop(0.5, '#1a1a4a'); grad.addColorStop(1, '#0d2d0d');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
  }
}

function drawParticles(ctx, particles, maxLife) {
  for (let i = particles.length - 1; i >= 0; i--) {
    const p = particles[i];
    p.x += p.vx; p.y += p.vy;
    if (p.vy !== undefined && Math.abs(p.vy) < 10) p.vy += 0.15;
    p.life--;
    if (p.life <= 0) { particles.splice(i, 1); continue; }
    ctx.globalAlpha = p.life / maxLife;
    ctx.fillStyle = p.color;
    ctx.beginPath(); ctx.arc(p.x, p.y, 3, 0, Math.PI * 2); ctx.fill();
  }
  ctx.globalAlpha = 1;
}

export default RunnerMode;
